import { motion } from "framer-motion";
import { GraduationCap, Award, Star, Briefcase, Users, BookOpen, Shield } from "lucide-react";

/* AuthorSection — Sobre o criador com foto, credenciais e citação */
const credentials = [
  { icon: GraduationCap, title: "Residência Multiprofissional em Oncologia", color: "text-accent" },
  { icon: Briefcase,     title: "Enfermeiro assistencial e supervisor oncológico", color: "text-primary" },
  { icon: Award,         title: "Coordenador na área oncológica", color: "text-accent" },
  { icon: Star,          title: "11+ anos de experiência em enfermagem", color: "text-warning" },
  { icon: Users,         title: "Referência para milhares de profissionais", color: "text-primary" },
  { icon: BookOpen,      title: "Criador da Plataforma Jornada Oncológica", color: "text-accent" },
];

export function AuthorSection() {
  return (
    <section className="py-16 sm:py-24 bg-card/30">
      <div className="container max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-14"
        >
          <span className="text-accent text-xs font-semibold uppercase tracking-widest mb-3 block">
            Sobre o criador
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
            Aprenda com Quem Vive a Oncologia
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">

          {/* Foto + nome */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center text-center"
          >
            <div className="relative mb-6">
              {/* Ring pulsante */}
              <motion.div
                animate={{ scale: [1, 1.06, 1], opacity: [0.4, 0.15, 0.4] }}
                transition={{ repeat: Infinity, duration: 3.5 }}
                className="absolute inset-0 rounded-full bg-accent/20 blur-md"
              />
              <div className="relative w-44 h-44 sm:w-56 sm:h-56 rounded-full overflow-hidden ring-4 ring-accent/30 shadow-2xl">
                <img
                  src="/images/premium_07_jaleco_bracos_cruzados_autoridade_1.png"
                  alt="Artur Cunha — Enfermeiro Oncologista"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-1">Artur Cunha</h3>
            <p className="text-accent font-medium text-sm sm:text-base mb-3">
              Enfermeiro Oncologista · Coordenador · Criador da Plataforma
            </p>
            <div className="flex gap-1 justify-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-warning text-warning" />
              ))}
            </div>
          </motion.div>

          {/* Credenciais + bio + citação */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {/* Grid de credenciais */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mb-6 sm:mb-7">
              {credentials.map((cred, index) => {
                const Icon = cred.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 + index * 0.07 }}
                    className="flex items-start gap-2.5 bg-card border border-border rounded-xl p-3 hover:border-accent/30 transition-colors"
                  >
                    <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${cred.color}`} />
                    <span className="text-xs text-foreground leading-snug">{cred.title}</span>
                  </motion.div>
                );
              })}
            </div>

            {/* Bio */}
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-4">
              Mais de 11 anos de experiência na enfermagem. Residência multiprofissional em oncologia. 
              Atuou como enfermeiro assistencial e supervisor. Atualmente coordenador na área oncológica. 
              Referência em conteúdo oncológico para milhares de profissionais.
            </p>

            {/* Parágrafo de diretrizes */}
            <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 mb-5 sm:mb-6 flex items-start gap-3">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-accent shrink-0 mt-0.5" />
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                Todo o conteúdo foi desenvolvido com base nas principais diretrizes científicas internacionais —
                ESMO, ASCO, INCA, ABHH e Surviving Sepsis Campaign — e validado na prática clínica diária.
                O objetivo é simples: ser seu manual de bolso digital, disponível a qualquer hora, em qualquer dispositivo.
              </p>
            </div>

            {/* Citação em bloco de destaque */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-accent/10 border-l-4 border-accent rounded-r-2xl p-4 sm:p-6 relative overflow-hidden"
            >
              <div className="absolute -top-4 -right-4 text-7xl text-accent/10 font-serif leading-none pointer-events-none select-none">
                "
              </div>
              <p className="relative z-10 text-foreground text-sm sm:text-base font-medium italic leading-relaxed mb-3">
                "Eu sei exatamente como você se sente. Já estive no seu lugar — sem direção, sem clareza.
                Foi isso que me motivou a criar esse guia. Meu objetivo é compartilhar o conhecimento prático que adquiri
                ao longo dos anos para que você possa atuar com mais confiança e segurança na oncologia."
              </p>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center">
                  <Award className="w-3.5 h-3.5 text-accent" />
                </div>
                <span className="text-accent text-xs font-semibold">— Artur Cunha</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
